import { useCallback, useEffect, useState } from "react";

/**
 * Camera / microphone / speaker enumeration with persisted preferences.
 * Device labels are only available after a getUserMedia grant — callers
 * should invoke `refresh()` after acquiring media.
 */

const STORAGE_KEY = "elg5:meeting-devices";

export interface DevicePrefs {
  cameraId: string | null;
  micId: string | null;
  speakerId: string | null;
}

export interface MediaDeviceOption {
  deviceId: string;
  label: string;
}

function loadPrefs(): DevicePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        cameraId: parsed.cameraId ?? null,
        micId: parsed.micId ?? null,
        speakerId: parsed.speakerId ?? null,
      };
    }
  } catch {
    /* corrupted prefs — fall through */
  }
  return { cameraId: null, micId: null, speakerId: null };
}

export const supportsSetSinkId = (): boolean =>
  typeof HTMLMediaElement !== "undefined" &&
  "setSinkId" in HTMLMediaElement.prototype;

/**
 * Audio- and video-ONLY constraints, kept separate on purpose.
 *
 * A combined `getUserMedia({audio, video})` rejects WHOLESALE when either
 * device is missing — so a machine with a mic but no camera fails with
 * NotFoundError and the browser never even prompts for the mic. Acquiring
 * audio and video in two independent calls isolates them: the mic prompt
 * appears and works regardless of whether a camera exists.
 */
export function audioConstraints(prefs: DevicePrefs): MediaStreamConstraints {
  return {
    audio: {
      ...(prefs.micId ? { deviceId: { exact: prefs.micId } } : {}),
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  };
}

export function videoConstraints(prefs: DevicePrefs): MediaStreamConstraints {
  return {
    video: {
      ...(prefs.cameraId ? { deviceId: { exact: prefs.cameraId } } : {}),
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 24, max: 30 },
    },
  };
}

export interface UseMediaDevices {
  cameras: MediaDeviceOption[];
  microphones: MediaDeviceOption[];
  speakers: MediaDeviceOption[];
  prefs: DevicePrefs;
  setPref: (kind: keyof DevicePrefs, deviceId: string | null) => void;
  refresh: () => Promise<void>;
  /** Prompt for camera/mic permission (best-effort) and refresh device labels. */
  requestPermission: () => Promise<boolean>;
  canPickSpeaker: boolean;
}

export function useMediaDevices(): UseMediaDevices {
  const [cameras, setCameras] = useState<MediaDeviceOption[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceOption[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceOption[]>([]);
  const [prefs, setPrefs] = useState<DevicePrefs>(loadPrefs);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const toOption = (d: MediaDeviceInfo, fallback: string): MediaDeviceOption => ({
        deviceId: d.deviceId,
        label: d.label || fallback,
      });
      setCameras(
        devices
          .filter((d) => d.kind === "videoinput")
          .map((d, i) => toOption(d, `Kamera ${i + 1}`)),
      );
      setMicrophones(
        devices
          .filter((d) => d.kind === "audioinput")
          .map((d, i) => toOption(d, `Mikrofon ${i + 1}`)),
      );
      setSpeakers(
        devices
          .filter((d) => d.kind === "audiooutput")
          .map((d, i) => toOption(d, `Głośnik ${i + 1}`)),
      );
    } catch {
      /* enumeration unavailable — pickers stay empty */
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onChange = () => void refresh();
    navigator.mediaDevices?.addEventListener?.("devicechange", onChange);
    return () =>
      navigator.mediaDevices?.removeEventListener?.("devicechange", onChange);
  }, [refresh]);

  const setPref = useCallback((kind: keyof DevicePrefs, deviceId: string | null) => {
    setPrefs((prev) => {
      const next = { ...prev, [kind]: deviceId };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* quota/private mode — prefs just won't persist */
      }
      return next;
    });
  }, []);

  // Pre-join permission prompt: acquire audio and video INDEPENDENTLY (a missing
  // camera must not block the mic prompt), immediately stop the tracks — we only
  // wanted the grant so device labels populate — then refresh the pickers.
  const requestPermission = useCallback(async (): Promise<boolean> => {
    let granted = false;
    for (const constraints of [
      { audio: true },
      { video: true },
    ] as MediaStreamConstraints[]) {
      try {
        const s = await navigator.mediaDevices.getUserMedia(constraints);
        s.getTracks().forEach((t) => t.stop());
        granted = true;
      } catch {
        /* device absent or denied — try the other kind */
      }
    }
    await refresh();
    return granted;
  }, [refresh]);

  return {
    cameras,
    microphones,
    speakers,
    prefs,
    setPref,
    refresh,
    requestPermission,
    canPickSpeaker: supportsSetSinkId(),
  };
}
